// @flow

import React from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { localize } from "redux-i18n";
import { Page } from "react-page-layout";
import { formatAuthoredBy } from "@transformers/names";
import { View, Text, Loader } from "@components";
import sharedActionCreators from "@modules/shared/actionCreator";
import { getRouteParams } from "@selectors";
import withErrorHandler from "@components/error";
import type { State } from "@store/reducers";
import { withClientContext, type PropsFromClientContext } from "@modules/context/components";
import { getEmployee, getActionSummary } from "@modules/employee/selectors";
import type { EmployeeActionSummaryType, EmployeeType } from "@modules/employee/types";
import {
  withPermissions,
  PermissionEnum,
  type PropsFromPermissions,
} from "@modules/account/components";
import actionCreators from "@modules/reporting/actionCreator";
import employeeActionCreators from "@modules/employee/actionCreator";
import { EmployeeDocumentsList } from "@modules/reporting/components";
import { EmployeeSpecificDocumentsList } from "@modules/reporting/components";
import { DocumentsSummary } from "@modules/shared/components";
import * as selectors from "@modules/shared/selectors/employee-events";
import { isLoading } from "@modules/shared/selectors";
import type {
  DocumentType,
  SpecificDocumentType,
} from "@modules/shared/reducer/employee-events/types";

type PropsFromState = $Exact<{
  clientId: number,
  employeeId: number,
  employee: ?EmployeeType,
  isReportingPage: boolean,
  isLoading: boolean,
  documents: Array<DocumentType>,
  specificDocuments: Array<SpecificDocumentType>,
  actionSummary?: {
    numberToManager: string,
    values: Array<EmployeeActionSummaryType>,
  } | null,
}>;

type PropsFromDispatch = $Exact<{
  getEmployees: typeof employeeActionCreators.GetEmployees,
  getActionSummary: typeof employeeActionCreators.GetEmployeeActionSummary,
}>;

type Props = $Exact<{
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
  ...PropsFromClientContext,
  ...PropsFromPermissions,
}>;

export class EmployeeDocuments extends React.Component<Props> {
  componentDidMount() {
    this.props.getEmployees({
      clientId: this.props.clientId,
      filters: {
        id: this.props.employeeId,
      },
    });
    this.props.getActionSummary({
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
    });
  }

  get getTotalFitNotes() {
    const notes = [...(this.props.documents || []), ...(this.props.specificDocuments || [])];
    const fitNotes = notes.filter(
      ({ fileType, documentType }: $Exact<DocumentType & SpecificDocumentType>) =>
        fileType === "FIT_NOTE" || documentType === "FIT_NOTE",
    );

    return fitNotes.length;
  }

  render() {
    return (
      <View position="relative" gap={6}>
        <Text className="message-box" text={this.props.t("EMPLOYEE_DOCUMENTS_TAB_INFO")} />

        {this.props.isLoading && <Loader inline />}

        <EmployeeDocumentsList
          isAddDocumentModalOpen={false}
          employeeId={this.props.employeeId}
          clientId={this.props.clientId}
        />
        <EmployeeSpecificDocumentsList
          employeeId={this.props.employeeId}
          clientId={this.props.clientId}
        />
      </View>
    );
  }
}

const mapStateToProps = (state: State, ownProps): PropsFromState => {
  const routeParams = getRouteParams(ownProps);
  const employeeId = parseInt(routeParams.employeeId);
  const clientId = parseInt(routeParams.clientId, 10);
  const isReportingPage = (((ownProps || {}).location || {}).pathname || "").includes("reporting");

  return {
    clientId,
    employeeId,
    employee: getEmployee(employeeId)(state),
    isReportingPage,
    documents: selectors.getDocuments(state),
    specificDocuments: selectors.getSpecificDocuments(state),
    isLoading: isLoading(state),
    actionSummary: getActionSummary(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  getEmployees: employeeActionCreators.GetEmployees,
  getActionSummary: employeeActionCreators.GetEmployeeActionSummary,
};

export default compose(
  withErrorHandler(),
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
  withPermissions({ permissionId: PermissionEnum.ACCESS_REPORTING_EMPLOYEES }),
  withClientContext(),
)(EmployeeDocuments);
